import { useState, useCallback, useRef, useEffect } from 'react';
import { Message, SignalState, TurnPresence } from '../types';
import { streamMessage, sendMessage, getApiUrl, PendingTurnError, type ActiveMind } from '../services/api';
import { getEntityId } from '../services/auth';

/**
 * B-68. Thrown when the server marks a stream error `retryable: false`, meaning the turn's side
 * effects have ALREADY run server-side (anchors, action tags, memory writes, felt effects) and the
 * default "retry the whole request non-streaming" recovery would execute them a second time.
 * Distinct from a plain `Error` so the catch below can tell "the transport dropped, try again" from
 * "this turn is already spent, show the user what happened".
 */
export class NonRetryableStreamError extends Error {
  readonly nonRetryable = true;
  constructor(message: string) {
    super(message);
    this.name = 'NonRetryableStreamError';
  }
}

const STORAGE_KEY_PREFIX = 'hannah_chat_history';
const MAX_HISTORY = 50;
const PROACTIVE_POLL_INTERVAL = 5_000; // Fallback polling (tighter when SSE is down)
const MAX_RECONNECT_DELAY = 10_000; // Cap reconnect backoff at 10s

/** Per-entity + per-mind storage key so each conversation is separate */
function getStorageKey(mind: ActiveMind = 'hannah'): string {
  const entity = getEntityId();
  const base = entity && entity !== 'unknown' ? `${STORAGE_KEY_PREFIX}_${entity}` : STORAGE_KEY_PREFIX;
  return mind === 'hannah' ? base : `${base}_${mind}`;
}

function loadMessages(storageKey: string): Message[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored) as Message[];
  } catch { /* ignore */ }
  return [];
}

/**
 * A conversation's identity is its storage key: account + mind. A turn keeps the key it was sent
 * under for its whole life, because the view that sent it can be gone before it finishes (mind
 * switched, or signed out and another account signed in).
 */
const HISTORY_CHANGED_EVENT = 'hannah:chat-history-changed';

/** Turns still in flight, by conversation. Outlives the view that sent them; Stop works from any view of it. */
const turnsInFlight = new Map<string, AbortController>();

/**
 * An unresolved turn to Hannah's durable runtime, kept in localStorage per conversation (account + mind) so it survives
 * a reload. It carries the request id that names the event, so "check" asks about THE SAME event (no second inference)
 * instead of sending a new turn. Never re-sent automatically; cleared only by a delivered reply or "Stop tracking it".
 *   working  — the runtime said it is still on it (its last word, as of `detail`)
 *   accepted — the runtime has the request, but its reply has not reached this page
 *   unknown  — sent, and nothing confirmed whether the runtime received it
 */
export type PendingState = 'working' | 'accepted' | 'unknown';
export interface PendingTurn {
  requestId: string;
  state: PendingState;
  eventId: string | null;
  detail: string;
  apiMessages: Array<{ role: string; content: string }>;
}
const PENDING_KEY_PREFIX = 'hannah_pending_turn:';

function readPending(key: string): PendingTurn | null {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY_PREFIX + key) ?? 'null') as PendingTurn | null;
    return p && typeof p.requestId === 'string' && Array.isArray(p.apiMessages) ? p : null;
  } catch { return null; }
}
function writePending(key: string, p: PendingTurn): void {
  try { localStorage.setItem(PENDING_KEY_PREFIX + key, JSON.stringify(p)); }
  catch (e) { console.error('[chat] could not keep an unresolved runtime turn across reloads:', e); }
}
function clearPending(key: string): void {
  try { localStorage.removeItem(PENDING_KEY_PREFIX + key); } catch { /* nothing kept */ }
}

function announce(storageKey: string): void {
  window.dispatchEvent(new CustomEvent<string>(HISTORY_CHANGED_EVENT, { detail: storageKey }));
}

/** Append a late reply to the conversation it was sent from, whichever view is mounted now. */
function appendStoredMessage(storageKey: string, msg: Message): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...loadMessages(storageKey), msg].slice(-MAX_HISTORY)));
  } catch (e) {
    console.error('[chat] could not keep a late reply in its conversation history:', e);
  }
  announce(storageKey);
}

function buildContextWindow(allMsgs: Message[]): Array<{ role: string; content: string }> {
  if (allMsgs.length <= 30) {
    return allMsgs.map(m => ({ role: m.role, content: m.content }));
  }
  const opening = allMsgs.slice(0, 2).map(m => ({ role: m.role, content: m.content }));
  const skipped = allMsgs.length - 27;
  const gap = [
    { role: 'user' as const, content: `[Note: ${skipped} earlier messages not shown. Continue naturally.]` },
    { role: 'assistant' as const, content: '[Understood — continuing.]' },
  ];
  const recent = allMsgs.slice(-25).map(m => ({ role: m.role, content: m.content }));
  return [...opening, ...gap, ...recent];
}

/**
 * `runtimeRouted`: cortex reports Hannah's chat is routed to the durable runtime (GET /runtime/status routing=enabled).
 * Then a turn is put on record before its first request; otherwise only a runtime acknowledgement puts it there.
 */
export function useChat(token: string | null, mind: ActiveMind = 'hannah', runtimeRouted = false) {
  const storageKey = getStorageKey(mind);
  const runtimeRoutedRef = useRef(runtimeRouted);
  runtimeRoutedRef.current = runtimeRouted;
  const [messages, setMessages] = useState<Message[]>(() => loadMessages(storageKey));
  /** A turn for this conversation sent from an earlier view is still running. */
  const [turnElsewhere, setTurnElsewhere] = useState(() => turnsInFlight.has(storageKey));
  const [pendingTurn, setPendingTurn] = useState<PendingTurn | null>(() => readPending(storageKey));
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingPresence, setStreamingPresence] = useState<TurnPresence | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSignals, setLastSignals] = useState<SignalState | null>(null);
  const [sseConnected, setSseConnected] = useState(false);
  const seenProactiveIds = useRef<Set<string>>(new Set());
  const sseAbortRef = useRef<AbortController | null>(null);
  const pendingSignalsRef = useRef<Partial<SignalState> | null>(null);
  const pendingPresenceRef = useRef<TurnPresence | null>(null);
  /** The conversation this hook is currently showing; null once unmounted. */
  const shownKeyRef = useRef<string | null>(storageKey);

  useEffect(() => {
    shownKeyRef.current = storageKey;
    setTurnElsewhere(turnsInFlight.has(storageKey));
    setPendingTurn(readPending(storageKey));
    const reload = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== storageKey) return;
      setMessages(loadMessages(storageKey));
      setTurnElsewhere(turnsInFlight.has(storageKey));
      setPendingTurn(readPending(storageKey));
    };
    window.addEventListener(HISTORY_CHANGED_EVENT, reload);
    return () => {
      shownKeyRef.current = null;
      window.removeEventListener(HISTORY_CHANGED_EVENT, reload);
    };
  }, [storageKey]);

  // Re-load messages when the conversation changes
  useEffect(() => {
    setMessages(loadMessages(storageKey));
    setLastSignals(null);
    seenProactiveIds.current.clear();
  }, [storageKey]);

  // SSE stream for real-time proactive messages (Feature 6 — Addendum XIII)
  // Falls back to polling if SSE connection fails
  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    let fallbackInterval: ReturnType<typeof setInterval> | null = null;
    let reconnectDelay = 1000;

    const addProactiveMessage = (msg: { id: string; content: string; type?: string; trigger?: string; timestamp?: number }) => {
      if (seenProactiveIds.current.has(msg.id)) return;
      if (!msg.content || !msg.content.trim()) return;
      seenProactiveIds.current.add(msg.id);

      const msgTimestamp = msg.timestamp || Date.now();
      setMessages(prev => {
        // Content-based dedup: skip if an identical message arrived in the last 60s
        // (catches concurrent-cron duplicates that have different IDs but same content)
        const isDuplicate = prev.some(m =>
          m.role === 'assistant' &&
          m.content === msg.content &&
          Math.abs(msgTimestamp - m.timestamp) < 60_000
        );
        if (isDuplicate) return prev;
        return [...prev, {
          id: msg.id,
          role: 'assistant',
          content: msg.content,
          timestamp: msgTimestamp,
          messageType: 'proactive' as const,
          trigger: msg.trigger,
        }];
      });
    };

    // Fallback: poll /proactive/pending when SSE is down
    const pollFallback = async () => {
      try {
        const res = await fetch(`${getApiUrl(mind)}/proactive/pending`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json() as {
          messages: Array<{ id: string; content: string; type: string; trigger: string; timestamp: number }>;
        };
        if (data.messages?.length) {
          for (const m of data.messages) addProactiveMessage(m);
        }
      } catch { /* silent */ }
    };

    const startFallbackPolling = () => {
      if (fallbackInterval) return;
      pollFallback(); // Immediate first poll
      fallbackInterval = setInterval(pollFallback, PROACTIVE_POLL_INTERVAL);
    };

    const stopFallbackPolling = () => {
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
        fallbackInterval = null;
      }
    };

    // SSE connection with auto-reconnect
    const connectSSE = async () => {
      if (cancelled) return;
      stopFallbackPolling();

      const controller = new AbortController();
      sseAbortRef.current = controller;

      try {
        const res = await fetch(`${getApiUrl(mind)}/v1/stream`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          throw new Error(`SSE connection failed: ${res.status}`);
        }

        reconnectDelay = 1000; // Reset on successful connection
        setSseConnected(true);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;

            const lines = eventBlock.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.slice(7);
              else if (line.startsWith('data: ')) eventData = line.slice(6);
            }

            if (!eventType || !eventData) continue;

            try {
              const parsed = JSON.parse(eventData);

              switch (eventType) {
                case 'message':
                  addProactiveMessage(parsed as { id: string; content: string; type?: string; trigger?: string; timestamp?: number });
                  break;
                case 'state':
                  // Live signal updates from cortex SSE stream
                  if (parsed.signals) {
                    setLastSignals(parsed.signals as SignalState);
                  }
                  break;
                case 'reconnect':
                  // Server requesting we reconnect
                  break;
                case 'ping':
                  // Keepalive — no action needed
                  break;
              }
            } catch { /* malformed event */ }
          }
        }
      } catch (e) {
        if (cancelled) return;
        setSseConnected(false);
        console.warn('[sse] Connection lost, will reconnect:', e instanceof Error ? e.message : e);
      }

      // Reconnect with backoff (or fall back to polling)
      if (!cancelled) {
        setSseConnected(false);
        startFallbackPolling(); // Poll while waiting to reconnect
        setTimeout(() => {
          stopFallbackPolling();
          reconnectDelay = Math.min(reconnectDelay * 1.5, MAX_RECONNECT_DELAY);
          connectSSE();
        }, reconnectDelay);
      }
    };

    connectSSE();

    return () => {
      cancelled = true;
      stopFallbackPolling();
      if (sseAbortRef.current) {
        sseAbortRef.current.abort();
        sseAbortRef.current = null;
      }
    };
  }, [token, mind]);

  // Persist messages (per-entity)
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages.slice(-MAX_HISTORY)));
    } catch { /* ignore */ }
  }, [messages, storageKey]);

  const send = useCallback(async (content: string) => {
    // One turn at a time per conversation, including one sent from a view that has since gone, and none while a
    // turn the runtime accepted is still unresolved here (check again, or dismiss it, first).
    if (!token || !content.trim() || isStreaming || turnsInFlight.has(storageKey) || readPending(storageKey)) return;
    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    // ONE id for this user turn, minted once: the streaming request and the non-streaming retry both carry it, so
    // the durable runtime sees one event however many transports it took. The next send gets a new one.
    await runTurn(storageKey, buildContextWindow([...messages, userMsg]), crypto.randomUUID());
  }, [token, messages, isStreaming, storageKey]);

  /** Ask again about a turn the runtime accepted: same request id, so the same event, never a new turn. */
  const checkPending = useCallback(async () => {
    const p = readPending(storageKey);
    if (!p || isStreaming || turnsInFlight.has(storageKey)) return;
    setPendingTurn(null);   // shown as waiting again; the record stays until a reply is delivered
    await runTurn(storageKey, p.apiMessages, p.requestId);
  }, [storageKey, isStreaming]);

  /** Stop tracking a pending turn here. The runtime keeps it; its reply is still recorded in her history there. */
  const dismissPending = useCallback(() => {
    clearPending(storageKey);
    setPendingTurn(null);
  }, [storageKey]);

  async function runTurn(key: string, apiMessages: Array<{ role: string; content: string }>, requestId: string) {
    if (!token) return;
    // Only Hannah's cortex has the durable runtime route; Amanda's and legacy turns are never put on record or re-asked.
    const runtimeTurn = mind === 'hannah';
    const tracked = () => readPending(key)?.requestId === requestId;
    /** Put (or update) this turn's record. Shown only when the wait is over, by the caller. */
    const keep = (state: PendingState, detail: string, eventId: string | null = null): PendingTurn => {
      const p = { requestId, apiMessages, state, detail, eventId: eventId ?? readPending(key)?.eventId ?? null };
      writePending(key, p);
      return p;
    };
    const show = (p: PendingTurn) => { if (shownKeyRef.current === key) setPendingTurn(p); };
    /** Append a finished assistant turn to `key`, even if this view (or this account) has gone. Resolves the record. */
    const deliver = (msg: Message) => {
      if (tracked()) clearPending(key);
      if (shownKeyRef.current === key) { setMessages(prev => [...prev, msg]); setPendingTurn(null); }
      else appendStoredMessage(key, msg);
    };
    // Runtime selected: on record BEFORE the first request, so a reload, Stop or lost connection can still ask about it.
    if (runtimeTurn && runtimeRoutedRef.current && !tracked()) keep('unknown', 'no reply received yet');

    setError(null);
    setIsStreaming(true);
    setStreamingContent('');
    setStreamingPresence(null);
    pendingPresenceRef.current = null;
    pendingSignalsRef.current = null;

    const abortController = new AbortController();
    turnsInFlight.set(key, abortController);
    let accumulated = '';
    let pending: { eventId: string; detail: string; outcome?: 'unavailable' } | null = null;

    try {
      for await (const event of streamMessage(apiMessages, token, abortController.signal, mind, requestId)) {
        if (event.type === 'accepted') {
          if (runtimeTurn) keep(readPending(key)?.state === 'working' ? 'working' : 'accepted', 'no reply received yet');
          continue;
        }
        if (event.type === 'pending') {
          pending = { eventId: event.eventId ?? '', detail: event.text ?? 'still processing', outcome: event.outcome };
          break;
        }
        if (event.type === 'signals' && event.data) {
          setLastSignals(event.data as SignalState);
          pendingSignalsRef.current = event.data as Partial<SignalState>;
        } else if (event.type === 'presence' && event.data) {
          // Q-096: her presence for THIS turn — shown live while she writes, kept on the message.
          pendingPresenceRef.current = event.data as TurnPresence;
          setStreamingPresence(event.data as TurnPresence);
        } else if (event.type === 'delta' && event.text) {
          accumulated += event.text;
          setStreamingContent(accumulated);
        } else if (event.type === 'correction' && event.text) {
          // Voice model rescued the response — replace streamed text
          accumulated = event.text;
          setStreamingContent(accumulated);
        } else if (event.type === 'error') {
          if (event.retryable === false) {
            throw new NonRetryableStreamError(event.text || 'Stream error');
          }
          throw new Error(event.text || 'Stream error');
        }
        // 'done' — stream complete
      }

      if (pending) {
        show(keep(pending.outcome === 'unavailable' ? 'accepted' : 'working', pending.detail, pending.eventId));
      } else if (accumulated && accumulated.trim()) {
        deliver({
          id: crypto.randomUUID(),
          role: 'assistant',
          content: accumulated,
          timestamp: Date.now(),
          signals: pendingSignalsRef.current ?? undefined,
          presence: pendingPresenceRef.current ?? undefined,
          messageType: 'response',
        });
      } else if (!accumulated.trim()) {
        console.error('Streaming completed with empty/whitespace-only response');
        setError('Got an empty response. Please try again.');
      }
    } catch (err) {
      if (abortController.signal.aborted) {
        // User stopped the stream — keep what we have
        if (accumulated && accumulated.trim()) {
          deliver({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: accumulated,
            timestamp: Date.now(),
            signals: pendingSignalsRef.current ?? undefined,
            messageType: 'response',
            stopped: true,
          });
        } else if (tracked()) {
          // Stop ends OUR wait, not the runtime's work: keep the same request so its reply can still be asked for.
          const was = readPending(key)!.state;
          show(keep(was === 'unknown' ? 'unknown' : 'accepted', 'you stopped waiting; the runtime may still finish'));
        } else {
          // Stop ends OUR wait. It cannot recall a turn the server already accepted, so say so.
          setError('Stopped waiting for this reply. Work the server already started may still finish; nothing was re-sent.');
        }
      } else if (err instanceof PendingTurnError) {
        // The runtime holds this event (its 503 `unavailable` on the streaming request): never a fresh send.
        show(keep(err.outcome === 'unavailable' ? 'accepted' : 'working', err.detail, err.eventId));
      } else if (err instanceof NonRetryableStreamError) {
        // B-68. The server has told us this turn already ran its side effects — her anchors, action
        // tags, memory writes and felt effects are written. Re-sending would execute them a second
        // time (streaming requests are not in the server's dedup cache) and the user would see a
        // normal-looking reply with no sign anything went wrong. Surface it instead of hiding it.
        console.error('Stream failed non-retryably — NOT replaying the turn:', err);
        if (tracked()) clearPending(key);   // no eventId: not the runtime's turn, so there is nothing to ask about
        setError(err.message);
      } else {
        console.error('Streaming failed, trying non-streaming:', err);
        try {
          // Same requestId: to the durable runtime this is the same event, not a second turn.
          const response = await sendMessage(apiMessages, token, mind, requestId);
          if (response && response.trim()) {
            deliver({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: response,
              timestamp: Date.now(),
              messageType: 'response',
            });
          } else {
            console.error('Non-streaming returned empty response');
            setError('Got an empty response. Please try again.');
          }
        } catch (fallbackErr) {
          if (fallbackErr instanceof PendingTurnError) {
            show(keep(fallbackErr.outcome === 'unavailable' ? 'accepted' : 'working', fallbackErr.detail, fallbackErr.eventId));
          } else if (tracked()) {
            // Both transports failed and nothing confirmed delivery: say "unknown", keep the same request to ask again.
            const was = readPending(key)!.state;
            const why = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
            show(keep(was === 'unknown' ? 'unknown' : 'accepted', why));
          } else {
            console.error('Non-streaming also failed:', fallbackErr);
            const why = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
            setError(`Failed to send message (${why}). Please try again.`);
          }
        }
      }
    } finally {
      // Whatever ended the wait, an unresolved record is shown: it blocks new sends, so it must never sit hidden.
      if (tracked()) show(readPending(key)!);
      pendingSignalsRef.current = null;
      turnsInFlight.delete(key);
      // A view of this conversation mounted after this one left is waiting on this turn. Not
      // announced to ourselves: a reload here would race the reply appended above.
      if (shownKeyRef.current !== key) announce(key);
      setIsStreaming(false);
      setStreamingContent('');
    }
  }

  const clearHistory = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(storageKey);
    seenProactiveIds.current.clear();
  }, [storageKey]);

  /** Stops this conversation's turn, including one sent from a view that has since been replaced. */
  const abort = useCallback(() => {
    turnsInFlight.get(storageKey)?.abort();
  }, [storageKey]);

  return {
    messages,
    streamingContent,
    streamingPresence,
    isStreaming: isStreaming || turnElsewhere,
    error,
    lastSignals,
    sseConnected,
    send,
    abort,
    clearHistory,
    pendingTurn,
    checkPending,
    dismissPending,
  };
}
